import { processDataService } from '../../../services/processDataService'
import {
  ACTIVITY_TYPES,
  FIELD_CONSTRAINTS,
  FIELD_TYPES,
  LOGIC_GATE,
  PROCESS_ACTIVITY_STATUSES,
  TABLE_FIELD_CONSTRAINTS,
} from '../../../utility/constants'
import {
  getConstraintErrorId,
  getConstraintErrorIdFromFieldId,
  getFieldId,
  getFieldIdFromKey,
  getLegacySubfieldId,
  getReviewId,
  getSelectableTableId,
} from './utils'
import { applyLogicGate, constraintValidationWithOperation } from './validationUtil'

export const isReviewSwitchDisabled = (activityType, activityStatus, processModifiedBy, userId) => {
  if (activityType === ACTIVITY_TYPES.LONG_RUNNING) {
    return userId == processModifiedBy
  } else if (activityType === ACTIVITY_TYPES.DATA_ENTRY) {
    return true
  } else {
    return (
      activityType == ACTIVITY_TYPES.REVIEW && activityStatus === PROCESS_ACTIVITY_STATUSES.CLOSED
    )
  }
}

export const isFieldActive = (
  field,
  allValues,
  conditionalEnableDisable,
  activityType,
  activityStatus,
  version,
  previousProcessReviewData = {},
  reviewData,
  processModifiedBy,
  userId,
) => {
  if (activityType === ACTIVITY_TYPES.REVIEW) {
    return false
  } else if (activityType === ACTIVITY_TYPES.DATA_ENTRY && version === 1) {
    if (field?.is_read_only) {
      return false
    } else {
      return (
        activityStatus === PROCESS_ACTIVITY_STATUSES.ACTIVE &&
        isConditionallyEnabled(field.id, allValues, conditionalEnableDisable)
      )
    }
  } else if (activityType === ACTIVITY_TYPES.LONG_RUNNING && version === 1) {
    const isEnabledConditionally = isConditionallyEnabled(
      field.id,
      allValues,
      conditionalEnableDisable,
    )

    const isReviewRejected =
      reviewData && reviewData[getReviewId(field.id)]
        ? reviewData[getReviewId(field.id)] == (false || null)
        : true

    if (field?.is_read_only) {
      return false
    } else {
      return activityStatus == PROCESS_ACTIVITY_STATUSES.ACTIVE && userId == processModifiedBy
        ? isEnabledConditionally && isReviewRejected
        : false
    }
  } else if (activityType === ACTIVITY_TYPES.DATA_ENTRY && version > 1) {
    if (field?.is_read_only) {
      return false
    } else {
      return activityStatus == PROCESS_ACTIVITY_STATUSES.ACTIVE
        ? Object.keys(previousProcessReviewData).length > 0 &&
          Object.keys(previousProcessReviewData).includes(getReviewId(field.id)) &&
          previousProcessReviewData[getReviewId(field.id)] == (false || true)
          ? previousProcessReviewData[getReviewId(field.id)] == false &&
            isConditionallyEnabled(field.id, allValues, conditionalEnableDisable)
          : true
        : false
    }
  }
}

export const isConditionallyEnabled = (
  id,
  allValues,
  conditionalEnableDisable,
  fields = [],
  row,
) => {
  const sources = conditionalEnableDisable?.filter((it) => it.destination_field_template_id == id)
  if (sources?.length > 0) {
    const statuses = []
    for (const target of sources) {
      const sourceFieldId = target?.source_field_template_id
      const sourceField = fields?.find((it) => it.id == sourceFieldId)

      let fieldsProcessDataValue = allValues[getFieldId(target?.source_field_template_id)]
      if (sourceField?.is_subfield) {
        fieldsProcessDataValue = allValues[getLegacySubfieldId(sourceFieldId, row)]
      }

      const isEnabled = constraintValidationWithOperation(
        fieldsProcessDataValue,
        target?.enable_on_value,
        target?.operation,
      )

      statuses.push(isEnabled)
    }

    return applyLogicGate(statuses, LOGIC_GATE.AND)
  }
  return true
}

export const isFieldHidden = (field, hiddenFields = [], hiddenColumnIds = [], other = {}) => {
  const isHiddenForTeam = hiddenFields.find((it) => it.id == field.id)
  const isHiddenBySetting = hiddenColumnIds?.find((it) => it == field.id)

  let isHiddenByConditionalEnableDisable = !isConditionallyEnabled(
    field.id,
    other?.allValues,
    other?.conditionalEnableDisable,
    other?.fields,
    other?.row,
  )

  if (isHiddenByConditionalEnableDisable) {
    const target = other?.conditionalEnableDisable?.filter(
      (it) => it.destination_field_template_id == field.id,
    )
    if (target?.length > 0) {
      const is_destination_field_subfield = target[0].is_destination_field_subfield
      const is_source_field_subfield = target[0].is_source_field_subfield
      if (is_destination_field_subfield && is_source_field_subfield) {
        isHiddenByConditionalEnableDisable = false
      }
    }
  }

  const isFinallyHidden =
    field.is_hidden ||
    isHiddenForTeam != undefined ||
    isHiddenBySetting != undefined ||
    isHiddenByConditionalEnableDisable

  return isFinallyHidden
}

export const checkFieldConstraints = (
  field,
  value,
  constraints = [],
  allValues,
  rowNumber = null,
) => {
  const suffix = rowNumber ? `__${rowNumber}` : ''
  const validationArray = []

  const rules = field?.validation_rules

  // 1. VALIDATION RULES
  if (rules) {
    const type = field.data_structure
    const isEmpty = value === null || value === undefined || value === ''
    const numericValue = !isEmpty && !isNaN(Number(value)) ? Number(value) : null

    // NUMBER / CURRENCY RULES
    if (type === FIELD_TYPES.NUMBER || type === FIELD_TYPES.CURRENCY) {
      if (!isEmpty && numericValue !== null) {
        if (rules.min !== undefined && numericValue < rules.min) {
          return getError(
            field,
            suffix,
            rules.errorMessage || `${field.field_template_name} must be >= ${rules.min}`,
          )
        }

        if (rules.max !== undefined && numericValue > rules.max) {
          return getError(
            field,
            suffix,
            rules.errorMessage || `${field.field_template_name} must be <= ${rules.max}`,
          )
        }
      }
    }

    // TEXT / BIGTEXT RULES
    if ((type === FIELD_TYPES.TEXT || type === FIELD_TYPES.BIGTEXT) && !isEmpty) {
      const textValue = String(value)

      // min length
      if (rules.minLength !== undefined && textValue.length < Number(rules.minLength)) {
        return getError(
          field,
          suffix,
          rules.errorMessage ||
            `${field.field_template_name} must be at least ${rules.minLength} characters`,
        )
      }

      // max length
      if (rules.maxLength !== undefined && textValue.length > Number(rules.maxLength)) {
        return getError(
          field,
          suffix,
          rules.errorMessage ||
            `${field.field_template_name} must be at most ${rules.maxLength} characters`,
        )
      }

      // regex validation
      if (rules.regExPattern) {
        try {
          const regex =
            rules.regExPattern instanceof RegExp
              ? rules.regExPattern
              : new RegExp(rules.regExPattern)

          if (!regex.test(textValue)) {
            return getError(
              field,
              suffix,
              rules.errorMessage || `${field.field_template_name} format is invalid`,
            )
          }
        } catch {
          console.warn('Invalid regex in validation_rules:', rules.regExPattern)
        }
      }
    }
  }

  // 2. REQUIRED FIELD VALIDATION
  const isEmpty =
    value === null ||
    value === undefined ||
    value === '' ||
    value === getDefaultValueForField(field)

  if (field.is_required && isEmpty) {
    validationArray.push(`${field.field_template_name} is a required field!`)
  } else {
    const validations = constraints.filter((it) => it.field_template_id == field.id)

    if (validations.length === 0) {
      const finalErrorText = validationArray.join(', ')
      return getError(field, suffix, finalErrorText || null)
    }

    for (let validation of validations) {
      let errorMessage = null

      let relatedValue = 0

      if (validation.related_field_template_id != null && validation.constant_value == null) {
        relatedValue = allValues[`f_${validation.related_field_template_id}${suffix}`]
      } else if (
        validation.related_field_template_id == null &&
        validation.constant_value != null
      ) {
        relatedValue = validation.constant_value
      }

      switch (validation.constraint) {
        case FIELD_CONSTRAINTS.POSITIVE:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.POSITIVE)) {
            errorMessage = `${field.field_template_name} must be a positive number!`
          }
          break

        case FIELD_CONSTRAINTS.POSITIVE_INT:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.POSITIVE_INT)) {
            errorMessage = `${field.field_template_name} must be a positive integer!`
          }
          break

        case FIELD_CONSTRAINTS.INT:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.INT)) {
            errorMessage = `${field.field_template_name} must be an integer!`
          }
          break

        case FIELD_CONSTRAINTS.WHOLE_NUM:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.WHOLE_NUM)) {
            errorMessage = `${field.field_template_name} must be a whole number!`
          }
          break

        // RELATIONAL CONSTRAINTS
        case FIELD_CONSTRAINTS.EQUAL: {
          let isValid = false

          // TEXT / BIGTEXT exact string comparison
          if (
            field.data_structure === FIELD_TYPES.TEXT ||
            field.data_structure === FIELD_TYPES.BIGTEXT
          ) {
            isValid = String(value ?? '') === String(relatedValue ?? '')
          } else {
            // existing behavior for numbers etc.
            isValid = constraintValidationWithOperation(
              value,
              relatedValue,
              FIELD_CONSTRAINTS.EQUAL,
            )
          }

          if (!isValid) {
            errorMessage = `${field.field_template_name} must be equal to ${relatedValue}!`
          }

          break
        }

        case FIELD_CONSTRAINTS.LT:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.LT)) {
            errorMessage = `${field.field_template_name} must be less than ${relatedValue}!`
          }
          break

        case FIELD_CONSTRAINTS.LTE:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.LTE)) {
            errorMessage = `${field.field_template_name} must be less than or equal to ${relatedValue}!`
          }
          break

        case FIELD_CONSTRAINTS.GT:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.GT)) {
            errorMessage = `${field.field_template_name} must be greater than ${relatedValue}!`
          }
          break

        case FIELD_CONSTRAINTS.GTE:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.GTE)) {
            errorMessage = `${field.field_template_name} must be greater than or equal to ${relatedValue}!`
          }
          break

        default:
          console.log(`implementation for '${validation.constraint}' is missing`)
      }

      if (errorMessage) {
        validationArray.push(errorMessage)
      }
    }
  }

  const finalErrorText = validationArray.join(', ')

  return getError(field, suffix, finalErrorText ? finalErrorText.trim() : null)
}

const getError = (field, suffix, text) => {
  return {
    [`${getConstraintErrorId(field.id)}${suffix}`]: text,
  }
}

export const getDefaultValueForField = (field) => {
  switch (field.data_structure) {
    case FIELD_TYPES.LIST:
    case FIELD_TYPES.LIST_MULTI:
      return -1
    case FIELD_TYPES.NUMBER:
    case FIELD_TYPES.CURRENCY:
      return 0
    case FIELD_TYPES.TABLE:
      return 1
    case FIELD_TYPES.TIMESTAMPED_DATE:
    case FIELD_TYPES.TIMESTAMPED_DATETIME:
      return []
    default:
      return ''
  }
}

export const requiredFieldValidation = (
  user,
  fields,
  requiredFields,
  conditionalEnabling,
  allValues,
  selectableTableData,
) => {
  let constraintValidation = []

  const idFieldMap = fields.reduce((acc, cur) => {
    return { ...acc, [cur.id]: cur }
  }, {})

  const idFieldMapRequired = requiredFields.reduce((acc, cur) => {
    return { ...acc, [cur.id]: cur }
  }, {})

  Object.entries(allValues).forEach(([key, value]) => {
    if (!key.startsWith('f_')) return

    // check if key is still required or not
    const { id, row } = getFieldIdFromKey(key)
    const field = idFieldMapRequired[id]

    if (field) {
      const defaultVal = getDefaultValueForField(field)
      // TREAT NULL/UNDEFINED AS EMPTY
      // File fields in some tables are stored as null when empty, causing validation to be skipped.
      const isEmpty = value === defaultVal || value === null || value === undefined

      if (isEmpty) {
        // field is marked as required and have default/null value
        if (field?.is_subfield) {
          // check if table itself is disabled, if so, return
          const tableField = fields.find((it) => it.id === field.table_field_template_id)
          const tableItselfConditionallyDisabled = !isConditionallyEnabled(
            field.table_field_template_id,
            allValues,
            conditionalEnabling,
            fields,
          )

          if (
            tableItselfConditionallyDisabled ||
            tableField?.is_hidden ||
            tableField?.is_read_only
          ) {
            return
          }
        }

        const conditionallyDisabled = !isConditionallyEnabled(
          id,
          allValues,
          conditionalEnabling,
          fields,
          row,
        )

        let cellFromUnselectedRow = false
        if (
          field?.is_subfield &&
          selectableTableData[getSelectableTableId(field?.table_field_template_id)]
        ) {
          const selectedRows =
            selectableTableData[getSelectableTableId(field?.table_field_template_id)]?.map(Number)
          cellFromUnselectedRow = !selectedRows?.includes(String(row))
        }

        let isFieldVisible = true
        const visibleTeams = field.visible_team_id.filter((it) => it != null)
        const visibleTeamTypes = field.visible_team_type.filter((it) => it != null)

        if (visibleTeams?.length > 0 || visibleTeamTypes?.length > 0) {
          // user in restricted to team or team_type
          const isVisibleBasedOnTeam = visibleTeams?.some((teamId) => user.teams.includes(teamId))
          const isVisibleBasedOnTeamType = visibleTeamTypes?.some((teamType) =>
            user.team_types.includes(teamType),
          )

          isFieldVisible = isVisibleBasedOnTeam || isVisibleBasedOnTeamType
        }

        const finallyDisabled =
          field.is_hidden ||
          conditionallyDisabled ||
          field.is_read_only ||
          field.is_imported ||
          field.is_derived ||
          field.is_computed ||
          cellFromUnselectedRow ||
          !isFieldVisible

        if (!finallyDisabled) {
          constraintValidation.push({
            [getConstraintErrorIdFromFieldId(key)]: row
              ? `${idFieldMap[field?.table_field_template_id]?.field_template_name}/${
                  field.field_template_name
                } is a required field.`
              : `${field.field_template_name}" is a required field.`,
          })
        }
      }
    }
  })

  return constraintValidation
}

export const finalConstraintsValidation = async (
  tableConstraints,
  fieldConstraints,
  allSubfields,
  listDataMetadata,
  allValues,
  selectableTableData,
  user,
  processId,
) => {
  let constraintValidation = []

  // Track which subfield IDs are already handled inside the tableConstraints loop
  const validatedSubfieldIds = new Set(tableConstraints.map((tc) => tc.subfield_template_id))

  /* ========================== table field validations ========================= */
  for (let tableConstraint of tableConstraints) {
    const subFieldTemplateId = tableConstraint.subfield_template_id
    const tableFieldTemplateName = tableConstraint.table_name
    const isTableFieldSelectable = tableConstraint.is_selectable_table
    const subFieldTemplateName = tableConstraint.subfield_name
    const pivotFieldTemplateId = tableConstraint.pivot_field_template_id

    const constantValue = Number(tableConstraint.constant_value)
    const constraint = tableConstraint.constraint

    // FIND SUBFIELD CONFIG
    const subField = allSubfields.find((it) => it.id === subFieldTemplateId)

    if (pivotFieldTemplateId) {
      try {
        const result = (
          await processDataService.tableSubfieldValidationAcrossProcess(
            user,
            tableConstraint.id,
            processId,
          )
        ).data

        constraintValidation = [...constraintValidation, ...result]
      } catch (error) {
        console.error(error.message)
      }
    } else {
      let allSubfieldValues = Object.entries(allValues).filter(([key]) => {
        return key.includes(getFieldId(subFieldTemplateId) + '__')
      })

      /* ========================== selectable tables ========================== */

      if (isTableFieldSelectable) {
        let selectedRows = selectableTableData['st_' + tableConstraint.table_field_template_id]

        allSubfieldValues = Object.entries(allValues).filter(([key]) => {
          if (key.includes(getFieldId(subFieldTemplateId) + '__')) {
            let rowNum = key.split('__')[1]

            if (selectedRows?.includes(rowNum)) {
              return true
            }
          }

          return false
        })
      }

      let tempValues = []

      /* ========================== UNIQUE ========================== */

      if (constraint == TABLE_FIELD_CONSTRAINTS.UNIQUE) {
        for (const [key, value] of allSubfieldValues) {
          const isEmpty = value === null || value === undefined || value === ''

          // IGNORE EMPTY VALUES
          if (isEmpty) continue

          if (tempValues.includes(value)) {
            constraintValidation.push({
              [getConstraintErrorIdFromFieldId(key)]:
                `"${tableFieldTemplateName} / ${subFieldTemplateName}" must be unique across table.`,
            })
          }

          tempValues.push(value)
        }
      }

      /* ========================== MIN 1 MATCH ========================== */

      if (constraint == TABLE_FIELD_CONSTRAINTS.MIN_1_MATCH) {
        let isValue = allSubfieldValues.some(([, value]) => {
          return Number(value) === Number(constantValue)
        })

        let matchText = null

        // Only try metadata if it's a LIST field
        if (subField?.data_structure === FIELD_TYPES.LIST) {
          const matchObj = listDataMetadata.find(
            (it) => Number(it.list_data_id) === Number(constantValue),
          )
          matchText = matchObj?.list_data_value
        }

        const displayValue = matchText || constantValue

        const message = `"${tableFieldTemplateName}/${subFieldTemplateName}" must have at least one value of "${displayValue}".`

        if (isValue === false) {
          constraintValidation.push({
            [getConstraintErrorId(subFieldTemplateId)]: message,
          })
        }
      }

      /* ========================== VALIDATION RULES ========================== */

      const rules = subField?.validation_rules

      if (rules) {
        for (const [key, value] of allSubfieldValues) {
          const isEmpty = value === null || value === undefined || value === ''

          const dataType = subField?.data_structure

          const numericValue = !isEmpty && !isNaN(Number(value)) ? Number(value) : null

          let errorMessage = null

          /* ========================== NUMBER / CURRENCY ========================== */

          if (dataType === FIELD_TYPES.NUMBER || dataType === FIELD_TYPES.CURRENCY) {
            if (!isEmpty && numericValue !== null) {
              // MIN
              if (rules.min !== undefined && numericValue < Number(rules.min)) {
                errorMessage =
                  rules.errorMessage ||
                  `${tableFieldTemplateName} / ${subFieldTemplateName} must be >= ${rules.min}`
              }

              // MAX
              if (!errorMessage && rules.max !== undefined && numericValue > Number(rules.max)) {
                errorMessage =
                  rules.errorMessage ||
                  `${tableFieldTemplateName} / ${subFieldTemplateName} must be <= ${rules.max}`
              }
            }
          }

          /* ========================== TEXT / BIGTEXT ========================== */
          if ((dataType === FIELD_TYPES.TEXT || dataType === FIELD_TYPES.BIGTEXT) && !isEmpty) {
            const textValue = String(value)

            // MIN LENGTH
            if (rules.minLength !== undefined && textValue.length < Number(rules.minLength)) {
              errorMessage =
                rules.errorMessage ||
                `${tableFieldTemplateName} / ${subFieldTemplateName} must be at least ${rules.minLength} characters`
            }

            // MAX LENGTH
            if (
              !errorMessage &&
              rules.maxLength !== undefined &&
              textValue.length > Number(rules.maxLength)
            ) {
              errorMessage =
                rules.errorMessage ||
                `${tableFieldTemplateName} / ${subFieldTemplateName} must be at most ${rules.maxLength} characters`
            }

            // REGEX
            if (!errorMessage && rules.regExPattern) {
              try {
                const regex =
                  rules.regExPattern instanceof RegExp
                    ? rules.regExPattern
                    : new RegExp(rules.regExPattern)

                if (!regex.test(textValue)) {
                  errorMessage =
                    rules.errorMessage ||
                    `${tableFieldTemplateName} / ${subFieldTemplateName} format is invalid`
                }
              } catch {
                console.warn('Invalid regex in validation_rules:', rules.regExPattern)
              }
            }
          }

          /* ========================== PUSH ERROR ========================== */

          if (errorMessage) {
            constraintValidation.push({
              [getConstraintErrorIdFromFieldId(key)]: errorMessage,
            })
          }
        }
      }

      /* ========================== FIELD CONSTRAINTS FOR SUBFIELDS ========================== */

      const associatedConstraints = fieldConstraints.filter(
        (it) => it?.field_template_id === subFieldTemplateId,
      )

      for (const [key, value] of allSubfieldValues) {
        const row = key.split('__')[1]

        const error = checkFieldConstraints(
          {
            ...subField,
            field_template_name: `"${tableFieldTemplateName} / ${subFieldTemplateName}"`,
          },
          value,
          associatedConstraints,
          allValues,
          row,
        )

        if (error && Object.values(error)[0]) {
          constraintValidation.push(error)
        }
      }
    }
  }

  /* ========================== normal field validations =========================== */

  const fields = allSubfields.map((ft) => {
    if (ft.is_subfield) {
      const tableField = allSubfields.find((it) => it.id === ft?.table_field_template_id)

      return {
        ...ft,
        field_template_name: `"${tableField?.field_template_name} / ${ft?.field_template_name}"`,
      }
    } else {
      return ft
    }
  })

  for (const field of fields) {
    /* ========================== Subfields Validation ========================== */
    if (field?.is_subfield && validatedSubfieldIds.has(field.id)) {
      continue
    }

    const associatedConstraints = fieldConstraints.filter(
      (it) => it?.field_template_id === field.id,
    )

    const hasValidationRules =
      field?.validation_rules && Object.keys(field.validation_rules || {}).length > 0

    // Skip only if BOTH are absent
    if (associatedConstraints.length <= 0 && !hasValidationRules) {
      continue
    }

    const allFieldValues = Object.entries(allValues).reduce((acc, [key, value]) => {
      const keyDetails = getFieldIdFromKey(key)

      if (keyDetails?.id === field.id) {
        return [...acc, { ...keyDetails, value }]
      }

      return acc
    }, [])

    for (const { row, value } of allFieldValues) {
      const error = checkFieldConstraints(field, value, associatedConstraints, allValues, row)

      if (error && Object.values(error)[0]) {
        constraintValidation.push(error)
      }
    }
  }

  return constraintValidation
}

export const getFieldForSetting = (columns = []) => {
  let columnsForSetting = []
  for (let i = 0; i < columns.length; i++) {
    columnsForSetting.push({
      path: '' + columns[i].id,
      name: columns[i].field_template_name,
      is_hidden: columns[i].is_hidden,
    })
  }
  return columnsForSetting
}

export const getColumnForSetting = (columns = []) => {
  let ActivityColumnsForSetting = []
  for (let i = 0; i < columns.length; i++) {
    ActivityColumnsForSetting.push({
      path: '' + columns[i].id,
      name: columns[i].activity_template_name,
      is_hidden: columns[i].is_hidden,
    })
  }
  return ActivityColumnsForSetting
}
